import * as THREE from 'three';
import type { SnapSettings } from '../store/modelStore';
import {
  connectorsCompatible,
  getConnectorWorldDir,
  getConnectorWorldPos,
  getObjectConnectors,
  type Connector,
} from './connectors';
import { getWorldCoreBoxForObject } from './coreBounds';
import { snapValue } from './snap';

export type SnapPointType = 'StudMale' | 'TubeFemale' | 'SideStudMale' | 'SideTubeFemale' | 'Other';

export type BrickPose = {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
};

export type SnapPoint = {
  localPosition: THREE.Vector3;
  localNormal: THREE.Vector3;
  type: SnapPointType;
  connector: Connector;
  snapRadius: number;
  angularToleranceDeg: number;
};

export type SnapCandidate = {
  key: string;
  pose: BrickPose;
  source: 'snap';
  other: THREE.Object3D;
  a: Connector;
  b: Connector;
  aId: string;
  bId: string;
  aType: SnapPointType;
  bType: SnapPointType;
  aWorld: THREE.Vector3;
  bWorld: THREE.Vector3;
  alignmentDot: number;
  mouseOffset: number;
  rotationDeltaRad: number;
  raiseAmount: number;
  raiseIterations: number;
  maxPenetrationDepth: number;
  preSnapDeltaY: number;
  postSnapDeltaY: number;
  snapDelta: number;
  maleOnDrag: boolean | null;
  isStudPair: boolean;
  isVerticalStudTube: boolean;
  score: number;
};

export type ResolveResult = {
  pose: BrickPose;
  valid: boolean;
  raiseAmount: number;
  iterations: number;
  maxPenetrationDepth: number;
};

export type PreviousCandidateRef = {
  key: string;
  score: number;
};

export type DragSolveState = {
  previousCandidateKey: string | null;
  previousScore: number;
  previousY: number;
};

export type DragDebugData = {
  dragSnapPoints: THREE.Vector3[];
  nearbySnapPoints: THREE.Vector3[];
  finalSource: 'snap' | 'fallback';
  chosenPair: {
    aId: string;
    bId: string;
    aWorld: THREE.Vector3;
    bWorld: THREE.Vector3;
    maleId: string;
    femaleId: string;
    maleWorld: THREE.Vector3;
    femaleWorld: THREE.Vector3;
    delta: THREE.Vector3;
    deltaLen: number;
    isStudPair: boolean;
    preSnapDeltaY: number;
    postSnapDeltaY: number;
    raiseAmount: number;
    maxPenetrationDepth: number;
  } | null;
};

export type DragSolveResult = {
  targetXZ: THREE.Vector2;
  finalPose: BrickPose;
  fallbackPose: BrickPose;
  chosenCandidate: SnapCandidate | null;
  snapCandidates: SnapCandidate[];
  state: DragSolveState;
  debug: DragDebugData;
};

type CandidateKeyInfo = {
  otherKey: string;
  aKey: string;
  bKey: string;
  yawDeg: number;
};

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const DRAG_Y_EPSILON = 0.001;
const DEFAULT_GRID = 20;
const DEFAULT_SNAP_RADIUS = 12;
const DEFAULT_ANGULAR_TOLERANCE_DEG = 24;
const DEFAULT_MAX_RAISE_ITERATIONS = 10;
const DEFAULT_MAX_SNAP_CANDIDATES = 72;
const DEFAULT_BROAD_PHASE_RADIUS = 140;
const SNAP_LOCK_EPSILON = 1e-3;
const PENETRATION_EPSILON = 1e-4;
const STUD_SEATING_PENETRATION_TOLERANCE = 2.6;

const _qParent = new THREE.Quaternion();
const _qParentInv = new THREE.Quaternion();
const _qYaw = new THREE.Quaternion();
const _qCand = new THREE.Quaternion();
const _vA = new THREE.Vector3();
const _vB = new THREE.Vector3();
const _vC = new THREE.Vector3();
const _dragBox = new THREE.Box3();
const _otherBox = new THREE.Box3();

function clonePose(p: BrickPose): BrickPose {
  return { position: p.position.clone(), quaternion: p.quaternion.clone() };
}

export function CaptureBrickPose(obj: THREE.Object3D): BrickPose {
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  return {
    position: obj.getWorldPosition(new THREE.Vector3()),
    quaternion: obj.getWorldQuaternion(new THREE.Quaternion()),
  };
}

export function ApplyBrickPose(obj: THREE.Object3D, pose: BrickPose): void {
  const localPos = pose.position.clone();
  if (obj.parent) {
    obj.parent.getWorldQuaternion(_qParent);
    _qParentInv.copy(_qParent).invert();
    obj.quaternion.copy(_qParentInv).multiply(pose.quaternion);
    obj.parent.worldToLocal(localPos);
  } else {
    obj.quaternion.copy(pose.quaternion);
  }
  obj.position.copy(localPos);
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
}

function getGridStep(settings: SnapSettings): number {
  const raw = Number(settings.grid);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_GRID;
}

function getSnapRadius(settings: SnapSettings): number {
  const raw = Number(settings.connectorThreshold);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_SNAP_RADIUS;
}

function classifySnapPointType(connector: Connector, worldDir: THREE.Vector3): SnapPointType {
  const kind = String(connector.kind ?? '').toLowerCase();
  const group = String(connector.group ?? '').toLowerCase();
  const male = connector.gender === 'M' || kind === 'stud';
  const female = connector.gender === 'F' || kind === 'tube';
  const studFamily = kind === 'stud' || kind === 'tube' || group === 'stud';
  const vertical = Math.abs(worldDir.y) > 0.75;

  if (studFamily && male) return vertical ? 'StudMale' : 'SideStudMale';
  if (studFamily && female) return vertical ? 'TubeFemale' : 'SideTubeFemale';
  return 'Other';
}

function studTubeCompatible(aType: SnapPointType, bType: SnapPointType): boolean {
  if ((aType === 'StudMale' && bType === 'TubeFemale') || (aType === 'TubeFemale' && bType === 'StudMale')) {
    return true;
  }
  if (
    (aType === 'SideStudMale' && bType === 'SideTubeFemale') ||
    (aType === 'SideTubeFemale' && bType === 'SideStudMale')
  ) {
    return true;
  }
  return false;
}

function connectorsMatchForSnap(
  a: Connector,
  b: Connector,
  aType: SnapPointType,
  bType: SnapPointType
): boolean {
  if (!connectorsCompatible(a, b)) return false;

  const aStudLike = aType !== 'Other';
  const bStudLike = bType !== 'Other';
  if (aStudLike || bStudLike) {
    return studTubeCompatible(aType, bType);
  }
  return true;
}

function isAxisAlignedToUp(qWorld: THREE.Quaternion): boolean {
  const up = _vA.set(0, 1, 0).applyQuaternion(qWorld);
  const dot = Math.abs(up.dot(WORLD_UP));
  return dot > 0.98;
}

function buildDragSnapPoints(obj: THREE.Object3D, settings: SnapSettings): SnapPoint[] {
  const out: SnapPoint[] = [];
  const connectors = getObjectConnectors(obj);
  const radius = getSnapRadius(settings);

  for (const c of connectors) {
    const type = classifySnapPointType(c, c.dir.clone().normalize());
    out.push({
      localPosition: c.pos.clone(),
      localNormal: c.dir.clone().normalize(),
      type,
      connector: c,
      snapRadius: Math.max(radius, c.radius || radius),
      angularToleranceDeg: DEFAULT_ANGULAR_TOLERANCE_DEG,
    });
  }
  return out;
}

function overlapXZ(a: THREE.Box3, b: THREE.Box3): boolean {
  const xHit = a.min.x <= b.max.x && a.max.x >= b.min.x;
  const zHit = a.min.z <= b.max.z && a.max.z >= b.min.z;
  return xHit && zHit;
}

function normalizeYawDeg(rad: number): number {
  let deg = THREE.MathUtils.radToDeg(rad);
  deg = ((deg % 360) + 360) % 360;
  if (deg > 180) deg -= 360;
  return deg;
}

function buildCandidateKey(info: CandidateKeyInfo): string {
  return `${info.otherKey}|${info.aKey}|${info.bKey}|${info.yawDeg}`;
}

function connectorWorldPosForPose(connector: Connector, pose: BrickPose, out = new THREE.Vector3()): THREE.Vector3 {
  return out.copy(connector.pos).applyQuaternion(pose.quaternion).add(pose.position);
}

function strictIntersectionDepth(aMin: number, aMax: number, bMin: number, bMax: number): number {
  return Math.min(aMax, bMax) - Math.max(aMin, bMin);
}

type OverlapInfo = {
  overlapping: boolean;
  requiredRaise: number;
  maxPenetrationDepth: number;
};

function getCoreOverlapInfo(
  dragBrick: THREE.Object3D,
  others: THREE.Object3D[],
  eps = PENETRATION_EPSILON
): OverlapInfo {
  if (!getWorldCoreBoxForObject(dragBrick, _dragBox)) {
    return { overlapping: false, requiredRaise: 0, maxPenetrationDepth: 0 };
  }

  let overlapping = false;
  let requiredRaise = 0;
  let maxPenetrationDepth = 0;

  for (const other of others) {
    if (!other.visible) continue;
    if (!getWorldCoreBoxForObject(other, _otherBox)) continue;

    const ox = strictIntersectionDepth(_dragBox.min.x, _dragBox.max.x, _otherBox.min.x, _otherBox.max.x);
    if (ox <= eps) continue;
    const oy = strictIntersectionDepth(_dragBox.min.y, _dragBox.max.y, _otherBox.min.y, _otherBox.max.y);
    if (oy <= eps) continue;
    const oz = strictIntersectionDepth(_dragBox.min.z, _dragBox.max.z, _otherBox.min.z, _otherBox.max.z);
    if (oz <= eps) continue;
    overlapping = true;
    if (oy > maxPenetrationDepth) maxPenetrationDepth = oy;
    const raise = _otherBox.max.y - _dragBox.min.y + eps;
    if (raise > requiredRaise) requiredRaise = raise;
  }

  return { overlapping, requiredRaise, maxPenetrationDepth };
}

export function ComputeDragTargetXZ(mouseWorld: THREE.Vector3 | THREE.Vector2, settings: SnapSettings): THREE.Vector2 {
  const rawX = mouseWorld instanceof THREE.Vector2 ? mouseWorld.x : mouseWorld.x;
  const rawZ = mouseWorld instanceof THREE.Vector2 ? mouseWorld.y : mouseWorld.z;
  const step = getGridStep(settings);

  return new THREE.Vector2(
    snapValue(rawX, step),
    snapValue(rawZ, step)
  );
}

export function CollectNearbyBricks(
  xz: THREE.Vector2,
  radius: number,
  bricks: THREE.Object3D[]
): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  const rr = Math.max(1, radius);
  const rr2 = rr * rr;

  for (const obj of bricks) {
    if (!obj.visible) continue;

    _otherBox.setFromObject(obj);
    if (_otherBox.isEmpty()) continue;

    const cx = THREE.MathUtils.clamp(xz.x, _otherBox.min.x, _otherBox.max.x);
    const cz = THREE.MathUtils.clamp(xz.y, _otherBox.min.z, _otherBox.max.z);
    const dx = xz.x - cx;
    const dz = xz.y - cz;
    if (dx * dx + dz * dz <= rr2) out.push(obj);
  }

  return out;
}

function isOverlappingAtCurrentPose(dragBrick: THREE.Object3D, others: THREE.Object3D[]): boolean {
  return getCoreOverlapInfo(dragBrick, others).overlapping;
}

export function IsOverlapping(dragBrick: THREE.Object3D, pose: BrickPose, others: THREE.Object3D[]): boolean {
  const original = CaptureBrickPose(dragBrick);
  ApplyBrickPose(dragBrick, pose);
  const hit = isOverlappingAtCurrentPose(dragBrick, others);
  ApplyBrickPose(dragBrick, original);
  return hit;
}

export function ResolvePenetrationByRaising(
  dragBrick: THREE.Object3D,
  pose: BrickPose,
  others: THREE.Object3D[],
  opts?: {
    maxIterations?: number;
    epsilon?: number;
    minRaiseStep?: number;
    lockConnector?: Connector;
    lockTargetWorld?: THREE.Vector3;
    lockMaxDelta?: number;
  }
): ResolveResult {
  const maxIterations = Math.max(1, Math.floor(opts?.maxIterations ?? DEFAULT_MAX_RAISE_ITERATIONS));
  const epsilon = Math.max(1e-5, opts?.epsilon ?? DRAG_Y_EPSILON);
  const minRaiseStep = Math.max(0.05, opts?.minRaiseStep ?? 0.5);
  const lockMaxDelta = Math.max(1e-5, opts?.lockMaxDelta ?? SNAP_LOCK_EPSILON);
  const lockConnector = opts?.lockConnector;
  const lockTargetWorld = opts?.lockTargetWorld;
  const lockEnabled = !!lockConnector && !!lockTargetWorld;

  const original = CaptureBrickPose(dragBrick);
  const work = clonePose(pose);
  ApplyBrickPose(dragBrick, work);

  const lockBroken = () => {
    if (!lockEnabled) return false;
    const lockPos = getConnectorWorldPos(dragBrick, lockConnector!, _vC);
    return lockPos.distanceTo(lockTargetWorld!) > lockMaxDelta;
  };

  if (lockBroken()) {
    ApplyBrickPose(dragBrick, original);
    return { pose: work, valid: false, raiseAmount: 0, iterations: 0, maxPenetrationDepth: 0 };
  }

  let iterations = 0;
  let raised = 0;
  let maxPenetrationDepth = 0;

  while (iterations < maxIterations) {
    const overlapNow = getCoreOverlapInfo(dragBrick, others);
    if (!overlapNow.overlapping) {
      if (!lockEnabled) {
        work.position.y += epsilon;
        ApplyBrickPose(dragBrick, work);
        if (lockBroken()) {
          ApplyBrickPose(dragBrick, original);
          return { pose: work, valid: false, raiseAmount: raised, iterations, maxPenetrationDepth };
        }
      }
      if (!isOverlappingAtCurrentPose(dragBrick, others)) {
        ApplyBrickPose(dragBrick, original);
        return { pose: work, valid: true, raiseAmount: raised, iterations, maxPenetrationDepth };
      }
    }
    maxPenetrationDepth = Math.max(maxPenetrationDepth, overlapNow.maxPenetrationDepth);
    let raiseBy = overlapNow.requiredRaise > 0 ? overlapNow.requiredRaise : minRaiseStep;
    if (raiseBy < minRaiseStep) raiseBy = minRaiseStep;

    work.position.y += raiseBy;
    raised += raiseBy;
    iterations += 1;
    ApplyBrickPose(dragBrick, work);
    if (lockBroken()) {
      ApplyBrickPose(dragBrick, original);
      return { pose: work, valid: false, raiseAmount: raised, iterations, maxPenetrationDepth };
    }
  }

  const validNoOverlap = !isOverlappingAtCurrentPose(dragBrick, others);
  const validLock = !lockBroken();
  if (validNoOverlap && validLock && !lockEnabled) {
    work.position.y += epsilon;
    ApplyBrickPose(dragBrick, work);
  }

  ApplyBrickPose(dragBrick, original);
  return { pose: work, valid: validNoOverlap && validLock, raiseAmount: raised, iterations, maxPenetrationDepth };
}

export function ComputeStackedYForXZ(
  dragBrick: THREE.Object3D,
  xz: THREE.Vector2,
  previousY: number,
  nearbyBricks: THREE.Object3D[],
  poseQuaternionWorld?: THREE.Quaternion
): number {
  const original = CaptureBrickPose(dragBrick);
  const basePose: BrickPose = {
    position: new THREE.Vector3(xz.x, 0, xz.y),
    quaternion: (poseQuaternionWorld ?? original.quaternion).clone(),
  };
  ApplyBrickPose(dragBrick, basePose);

  if (!getWorldCoreBoxForObject(dragBrick, _dragBox)) {
    ApplyBrickPose(dragBrick, original);
    return Math.max(0, previousY || 0);
  }
  let supportY = 0;

  for (const other of nearbyBricks) {
    if (!other.visible) continue;
    if (!getWorldCoreBoxForObject(other, _otherBox)) continue;
    if (!overlapXZ(_dragBox, _otherBox)) continue;
    supportY = Math.max(supportY, _otherBox.max.y);
  }

  // Base solve is done with object world Y=0; adjust by current core min to find absolute world Y
  // where the dragged core bottom sits exactly on supportY/base plane.
  const baseCoreMinY = _dragBox.min.y;
  ApplyBrickPose(dragBrick, original);

  const y = supportY - baseCoreMinY;
  if (Number.isFinite(previousY) && Math.abs(y - previousY) < 0.01) return previousY;
  return y;
}

export function GenerateSnapCandidates(
  dragBrick: THREE.Object3D,
  nearbyBricks: THREE.Object3D[],
  xz: THREE.Vector2,
  settings: SnapSettings,
  collisionBricks: THREE.Object3D[],
  previousCandidateKey: string | null
): SnapCandidate[] {
  const dragSnapPoints = buildDragSnapPoints(dragBrick, settings);
  if (dragSnapPoints.length === 0) return [];
  if (nearbyBricks.length === 0) return [];

  const basePose = CaptureBrickPose(dragBrick);
  const threshold = getSnapRadius(settings);
  const planarGate = Math.max(threshold * 2.5, 28);
  const cosTol = Math.cos(THREE.MathUtils.degToRad(DEFAULT_ANGULAR_TOLERANCE_DEG));
  const yawSteps = isAxisAlignedToUp(basePose.quaternion)
    ? [0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]
    : [0];

  const byKey = new Map<string, SnapCandidate>();

  for (let ai = 0; ai < dragSnapPoints.length; ai++) {
    const aPoint = dragSnapPoints[ai];
    const aConn = aPoint.connector;
    const aId = aConn.id ?? `a${ai}`;

    for (const other of nearbyBricks) {
      if (!other.visible) continue;
      const othersConnectors = getObjectConnectors(other);
      if (!othersConnectors.length) continue;

      for (let bi = 0; bi < othersConnectors.length; bi++) {
        const bConn = othersConnectors[bi];
        const bId = bConn.id ?? `b${bi}`;
        const bPos = getConnectorWorldPos(other, bConn, new THREE.Vector3());
        const bDir = getConnectorWorldDir(other, bConn, new THREE.Vector3());

        for (const yaw of yawSteps) {
          _qYaw.setFromAxisAngle(WORLD_UP, yaw);
          _qCand.copy(_qYaw).multiply(basePose.quaternion);

          const aDirWorld = _vA.copy(aPoint.localNormal).applyQuaternion(_qCand).normalize();
          const alignmentDot = aDirWorld.dot(bDir);
          if (alignmentDot > -cosTol) continue;

          const aType = classifySnapPointType(aConn, aDirWorld);
          const bType = classifySnapPointType(bConn, bDir);
          if (!connectorsMatchForSnap(aConn, bConn, aType, bType)) continue;
          const isStudPair = studTubeCompatible(aType, bType);
          const isVerticalStudTube =
            isStudPair && Math.abs(aDirWorld.y) > 0.8 && Math.abs(bDir.y) > 0.8;

          // For vertical stud/tube stacking, force "dragged part on top":
          // dragged connector must be female tube, target connector must be male stud.
          if (isVerticalStudTube && !(aType === 'TubeFemale' && bType === 'StudMale')) continue;

          const aPosWorld = _vB.copy(aPoint.localPosition).applyQuaternion(_qCand).add(basePose.position);
          const candidatePos = basePose.position.clone().add(bPos.clone().sub(aPosWorld));
          candidatePos.x = snapValue(candidatePos.x, getGridStep(settings));
          candidatePos.z = snapValue(candidatePos.z, getGridStep(settings));

          const mouseOffset = Math.hypot(candidatePos.x - xz.x, candidatePos.z - xz.y);
          if (mouseOffset > planarGate) continue;

          const rawPose: BrickPose = {
            position: candidatePos,
            quaternion: _qCand.clone(),
          };
          const rawAWorld = connectorWorldPosForPose(aConn, rawPose, new THREE.Vector3());
          const preSnapDeltaY = rawAWorld.y - bPos.y;
          let resolved = ResolvePenetrationByRaising(dragBrick, rawPose, collisionBricks, {
            lockConnector: isStudPair ? aConn : undefined,
            lockTargetWorld: isStudPair ? bPos : undefined,
            lockMaxDelta: SNAP_LOCK_EPSILON,
          });
          const allowStudSeatingOverlap =
            isStudPair &&
            resolved.maxPenetrationDepth > 0 &&
            resolved.maxPenetrationDepth <= STUD_SEATING_PENETRATION_TOLERANCE;
          if (!resolved.valid) {
            if (!allowStudSeatingOverlap) continue;
            resolved = {
              pose: rawPose,
              valid: true,
              raiseAmount: 0,
              iterations: 0,
              maxPenetrationDepth: resolved.maxPenetrationDepth,
            };
          }
          if (IsOverlapping(dragBrick, resolved.pose, collisionBricks) && !allowStudSeatingOverlap) continue;

          const resolvedAWorld = connectorWorldPosForPose(aConn, resolved.pose, new THREE.Vector3());
          const postSnapDeltaY = resolvedAWorld.y - bPos.y;
          const snapDelta = resolvedAWorld.distanceTo(bPos);
          if (isStudPair && snapDelta > SNAP_LOCK_EPSILON) continue;

          const yawDeg = normalizeYawDeg(yaw);
          const yawPenaltyRad = Math.abs(THREE.MathUtils.degToRad(yawDeg));
          const key = buildCandidateKey({
            otherKey: other.uuid,
            aKey: aId,
            bKey: bId,
            yawDeg: Math.round(yawDeg),
          });

          const maleOnDrag =
            aType === 'StudMale' || aType === 'SideStudMale'
              ? true
              : aType === 'TubeFemale' || aType === 'SideTubeFemale'
                ? false
                : null;

          const candidate: SnapCandidate = {
            key,
            pose: resolved.pose,
            source: 'snap',
            other,
            a: aConn,
            b: bConn,
            aId,
            bId,
            aType,
            bType,
            aWorld: resolvedAWorld.clone(),
            bWorld: bPos.clone(),
            alignmentDot,
            mouseOffset,
            rotationDeltaRad: yawPenaltyRad,
            raiseAmount: resolved.raiseAmount,
            raiseIterations: resolved.iterations,
            maxPenetrationDepth: resolved.maxPenetrationDepth,
            preSnapDeltaY,
            postSnapDeltaY,
            snapDelta,
            maleOnDrag,
            isStudPair,
            isVerticalStudTube,
            score: 0,
          };

          const prev = byKey.get(key);
          if (!prev) {
            byKey.set(key, candidate);
            continue;
          }

          if (
            candidate.raiseAmount < prev.raiseAmount ||
            (candidate.raiseAmount === prev.raiseAmount && candidate.snapDelta < prev.snapDelta) ||
            (
              candidate.raiseAmount === prev.raiseAmount &&
              candidate.snapDelta === prev.snapDelta &&
              candidate.mouseOffset < prev.mouseOffset
            )
          ) {
            byKey.set(key, candidate);
          }
        }
      }
    }
  }

  const out = [...byKey.values()];
  out.sort((a, b) => {
    if (a.mouseOffset !== b.mouseOffset) return a.mouseOffset - b.mouseOffset;
    if (a.raiseAmount !== b.raiseAmount) return a.raiseAmount - b.raiseAmount;
    return a.key.localeCompare(b.key);
  });

  const capped = out.slice(0, DEFAULT_MAX_SNAP_CANDIDATES);
  const prevRef = previousCandidateKey ? { key: previousCandidateKey, score: 0 } : null;
  for (const c of capped) {
    c.score = ScoreCandidate(c, xz, prevRef);
  }
  return capped;
}

export function ScoreCandidate(
  candidate: SnapCandidate,
  xz: THREE.Vector2,
  previousCandidate: PreviousCandidateRef | null
): number {
  const planarOffset = Number.isFinite(candidate.mouseOffset)
    ? candidate.mouseOffset
    : Math.hypot(candidate.pose.position.x - xz.x, candidate.pose.position.z - xz.y);
  const alignment = THREE.MathUtils.clamp(-candidate.alignmentDot, 0, 1);
  const rotationPenalty = candidate.rotationDeltaRad;
  const raisePenalty = candidate.raiseAmount + candidate.raiseIterations * 0.8;
  const snapDeltaPenalty = candidate.snapDelta * (candidate.isStudPair ? 200 : 20);
  const verticalBonus = candidate.isVerticalStudTube ? 2.5 : 0;
  const hysteresisBonus = previousCandidate?.key === candidate.key ? 1.5 : 0;

  return (
    alignment * 30 +
    verticalBonus +
    hysteresisBonus -
    planarOffset * 0.7 -
    rotationPenalty * 7 -
    raisePenalty * 0.45 -
    snapDeltaPenalty
  );
}

function buildDebugData(
  dragBrick: THREE.Object3D,
  nearbyBricks: THREE.Object3D[],
  pose: BrickPose,
  chosenCandidate: SnapCandidate | null,
  finalSource: 'snap' | 'fallback'
): DragDebugData {
  const dragSnapPoints: THREE.Vector3[] = [];
  const dragConnectors = getObjectConnectors(dragBrick);
  for (const c of dragConnectors) {
    dragSnapPoints.push(c.pos.clone().applyQuaternion(pose.quaternion).add(pose.position));
  }

  const nearbySnapPoints: THREE.Vector3[] = [];
  for (const obj of nearbyBricks) {
    const cons = getObjectConnectors(obj);
    for (const c of cons) {
      if (nearbySnapPoints.length > 500) break;
      nearbySnapPoints.push(getConnectorWorldPos(obj, c, new THREE.Vector3()).clone());
    }
    if (nearbySnapPoints.length > 500) break;
  }

  return {
    dragSnapPoints,
    nearbySnapPoints,
    finalSource,
    chosenPair: (() => {
      if (!chosenCandidate) return null;

      const aWorld = connectorWorldPosForPose(chosenCandidate.a, pose, new THREE.Vector3());
      const bWorld = getConnectorWorldPos(chosenCandidate.other, chosenCandidate.b, new THREE.Vector3());

      const maleOnDrag = chosenCandidate.maleOnDrag;
      const maleWorld = (maleOnDrag ?? true) ? aWorld.clone() : bWorld.clone();
      const femaleWorld = (maleOnDrag ?? true) ? bWorld.clone() : aWorld.clone();
      const maleId = (maleOnDrag ?? true) ? chosenCandidate.aId : chosenCandidate.bId;
      const femaleId = (maleOnDrag ?? true) ? chosenCandidate.bId : chosenCandidate.aId;
      const delta = maleWorld.clone().sub(femaleWorld);

      return {
        aId: chosenCandidate.aId,
        bId: chosenCandidate.bId,
        aWorld,
        bWorld,
        maleId,
        femaleId,
        maleWorld,
        femaleWorld,
        delta,
        deltaLen: delta.length(),
        isStudPair: chosenCandidate.isStudPair,
        preSnapDeltaY: chosenCandidate.preSnapDeltaY,
        postSnapDeltaY: chosenCandidate.postSnapDeltaY,
        raiseAmount: chosenCandidate.raiseAmount,
        maxPenetrationDepth: chosenCandidate.maxPenetrationDepth,
      };
    })(),
  };
}

function selectBestCandidateWithHysteresis(
  candidates: SnapCandidate[],
  previousState: DragSolveState
): SnapCandidate | null {
  if (!candidates.length) return null;

  const sorted = candidates.slice().sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return a.key.localeCompare(b.key);
  });
  const best = sorted[0];
  if (!previousState.previousCandidateKey) return best;

  const prev = sorted.find((c) => c.key === previousState.previousCandidateKey);
  if (!prev) return best;
  if (best.key === prev.key) return best;

  const hysteresisBand = 1.2;
  if (prev.score >= best.score - hysteresisBand) return prev;
  return best;
}

export function SolveDragPlacement(
  dragBrick: THREE.Object3D,
  others: THREE.Object3D[],
  mouseWorld: THREE.Vector3,
  settings: SnapSettings,
  prevState: DragSolveState | null
): DragSolveResult {
  const currentPose = CaptureBrickPose(dragBrick);
  const targetXZ = ComputeDragTargetXZ(mouseWorld, settings);

  const broadPhaseRadius = Math.max(DEFAULT_BROAD_PHASE_RADIUS, getSnapRadius(settings) * 5);
  const nearbyBricks = CollectNearbyBricks(targetXZ, broadPhaseRadius, others);

  const priorState: DragSolveState = prevState ?? {
    previousCandidateKey: null,
    previousScore: 0,
    previousY: currentPose.position.y,
  };

  const candidates = GenerateSnapCandidates(
    dragBrick,
    nearbyBricks,
    targetXZ,
    settings,
    others,
    priorState.previousCandidateKey
  );

  const stackedY = ComputeStackedYForXZ(
    dragBrick,
    targetXZ,
    priorState.previousY,
    nearbyBricks,
    currentPose.quaternion
  );
  const fallbackRaw: BrickPose = {
    position: new THREE.Vector3(targetXZ.x, stackedY, targetXZ.y),
    quaternion: currentPose.quaternion.clone(),
  };
  const fallbackResolved = ResolvePenetrationByRaising(dragBrick, fallbackRaw, others);
  const fallbackPose = fallbackResolved.valid ? fallbackResolved.pose : fallbackRaw;

  let chosenCandidate = selectBestCandidateWithHysteresis(candidates, priorState);
  let finalPose = clonePose(fallbackPose);
  let finalSource: 'snap' | 'fallback' = 'fallback';

  if (chosenCandidate) {
    const candidatePose = chosenCandidate.pose;
    const candidateOverlaps = IsOverlapping(dragBrick, candidatePose, others);
    let candidateSnapDelta = 0;
    if (chosenCandidate.isStudPair) {
      const aWorld = connectorWorldPosForPose(chosenCandidate.a, candidatePose, _vA);
      const bWorld = getConnectorWorldPos(chosenCandidate.other, chosenCandidate.b, _vB);
      candidateSnapDelta = aWorld.distanceTo(bWorld);
    }

    if (!candidateOverlaps && (!chosenCandidate.isStudPair || candidateSnapDelta <= SNAP_LOCK_EPSILON)) {
      finalPose = candidatePose;
      finalSource = 'snap';
    } else {
      chosenCandidate = null;
      finalPose = fallbackPose;
      finalSource = 'fallback';
    }
  }

  const state: DragSolveState = {
    previousCandidateKey: chosenCandidate?.key ?? null,
    previousScore: chosenCandidate?.score ?? 0,
    previousY: finalPose.position.y,
  };

  return {
    targetXZ,
    finalPose,
    fallbackPose,
    chosenCandidate,
    snapCandidates: candidates,
    state,
    debug: buildDebugData(dragBrick, nearbyBricks, finalPose, chosenCandidate, finalSource),
  };
}
